import {
  MAX_ANSWER_LENGTH,
  TIMEOUT_ANSWER_TEXT,
  type ClientRoomState,
} from "@poppycock/shared";
import { motion } from "framer-motion";
import { useCallback, useRef, useState } from "react";
import { useAutoSubmitOnTimeout } from "../hooks/useAutoSubmitOnTimeout";
import { CaptionPrompt } from "./CaptionPrompt";
import { PhaseTimer } from "./PhaseTimer";
import { Button, Card, PhaseBadge, ProgressDots, TextArea } from "./ui";

export function WritingScreen({
  state,
  onSubmit,
}: {
  state: ClientRoomState;
  onSubmit: (text: string) => void;
}) {
  const [text, setText] = useState("");
  const textRef = useRef(text);
  textRef.current = text;
  const round = state.round!;
  const submitted = round.youSubmitted;
  const isCaption = round.kind === "caption";

  const forceSubmit = useCallback(() => {
    const draft = textRef.current.trim();
    onSubmit(draft || TIMEOUT_ANSWER_TEXT);
  }, [onSubmit]);

  const countdown = useAutoSubmitOnTimeout(
    state.phaseEndsAt,
    submitted,
    forceSubmit,
  );

  return (
    <div className="flex flex-1 flex-col">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs tracking-wide text-[var(--color-muted)] uppercase">
            Round {round.number}
          </p>
          <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
            {isCaption ? "Caption this nonsense" : "Write something unhinged"}
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PhaseTimer
            label={countdown.label}
            urgency={countdown.urgency}
            active={countdown.active && !submitted}
          />
          <PhaseBadge phase="writing" kind={isCaption ? "caption" : "text"} />
        </div>
      </header>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card strong className="mb-6">
          {isCaption && round.imageId ? (
            <CaptionPrompt imageId={round.imageId} category={round.category} />
          ) : (
            <>
              <p className="mb-2 text-xs tracking-[0.18em] text-[var(--color-accent)] uppercase">
                Category
              </p>
              <p className="text-xl leading-snug font-semibold tracking-tight sm:text-2xl">
                {round.category}
              </p>
            </>
          )}
        </Card>
      </motion.div>

      {submitted ? (
        <Card className="mb-6 flex-1">
          <div className="flex h-full flex-col items-center justify-center py-10 text-center">
            <p className="mb-2 text-lg font-semibold">
              {isCaption ? "Caption locked in" : "Answer locked in"}
            </p>
            <p className="mb-6 text-sm text-[var(--color-muted)]">
              Waiting on the slowpokes…
            </p>
            <ProgressDots
              submitted={round.submittedCount}
              total={round.totalPlayers}
            />
          </div>
        </Card>
      ) : (
        <Card className="mb-6 flex-1">
          <TextArea
            value={text}
            onChange={(e) =>
              setText(e.target.value.slice(0, MAX_ANSWER_LENGTH))
            }
            placeholder={
              isCaption ? "Your secret caption…" : "Your secret answer…"
            }
            rows={isCaption ? 3 : 4}
            maxLength={MAX_ANSWER_LENGTH}
            autoFocus
          />
          <div className="mt-2 flex justify-between text-xs text-[var(--color-muted)]">
            <span>
              {countdown.urgency !== "none"
                ? "Clock’s ticking — ships at 0:00 either way."
                : isCaption
                  ? "Funny. Mean. Wrong. Ship it."
                  : "Keep it short. Keep it weird."}
            </span>
            <span>
              {text.length}/{MAX_ANSWER_LENGTH}
            </span>
          </div>
        </Card>
      )}

      {!submitted && (
        <Button
          className="w-full"
          disabled={!text.trim()}
          onClick={() => onSubmit(text.trim())}
        >
          {isCaption ? "Submit caption" : "Submit answer"}
        </Button>
      )}
    </div>
  );
}
